import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EllipsisMenu } from 'app/components/menu/components/EllipsisMenu';
import { ActionsListItem } from 'app/components/menu/types';
import { DeleteDialog } from 'app/components/dialogs/DeleteDialog';
import {
  ChangeUserFleetPermissionsDialog,
  ChangeUserPermissionsDialog,
} from 'app/components/access-sharing/dialogs';
import { SharedItem, SharedItemShareType } from 'services/api/shares';
import { useNotificationsContext } from 'context';
import { ErrorNotification, Success } from 'app/components/Notifications';
import { IEquipmentEntry } from 'types/IEquipmentEntry';
import { SharedUserAccess } from 'app/components/access-sharing/cards/SharedUserAccessCard/types';
import { invitationsApi } from 'services/api/invitations/queries';

interface IOperatorDetailsActions {
  exposedAccessSharingRecordId: string;
  shareType: SharedItemShareType;
  userEmail: string;
  itemLabel: string;
  item?: IEquipmentEntry | SharedItem;
  userAccess: SharedUserAccess;
  onRevokeSuccess?: () => void;
}

const OperatorDetailsActions: React.FC<IOperatorDetailsActions> = ({
  exposedAccessSharingRecordId,
  shareType,
  userEmail,
  itemLabel,
  item,
  userAccess,
  onRevokeSuccess,
}) => {
  const { t } = useTranslation();
  const { addNotification } = useNotificationsContext();
  const [revokeInvitation] = invitationsApi.useRevokeInvitationMutation();
  const [isRevokeAccessDialogOpen, setIsRevokeAccessDialogOpen] =
    useState(false);
  const [isEditPermissionsDialogOpen, setIsEditPermissionsDialogOpen] =
    useState(false);

  const handleRevokeAccess = async () => {
    try {
      await revokeInvitation([
        {
          shareType,
          exposedAccessSharingRecordId,
        },
      ]);

      addNotification(
        <Success text={t('access_sharing.snackbars.access-revoked')} />,
      );

      onRevokeSuccess?.();
    } catch (error) {
      console.error(error);
      addNotification(<ErrorNotification text={'Server error'} />);
    } finally {
      setIsRevokeAccessDialogOpen(false);
    }
  };

  const handleEditPermissions = () => {
    setIsEditPermissionsDialogOpen(true);
  };

  const handleEditPermissionsClose = () => {
    setIsEditPermissionsDialogOpen(false);
  };

  const actions: ActionsListItem[] = [
    {
      label: t('access_sharing.role_based_actions.change_permissions'),
      action: handleEditPermissions,
    },
    {
      label: t('access_sharing.role_based_actions.revoke_access'),
      action: () => setIsRevokeAccessDialogOpen(true),
    },
  ];

  return (
    <>
      <DeleteDialog
        open={isRevokeAccessDialogOpen}
        title={t('revoke_access_confirmation', {
          email: userEmail,
          machineName: itemLabel,
        })}
        confirmationLabel={t('access_sharing.role_based_actions.revoke')}
        onClose={() => setIsRevokeAccessDialogOpen(false)}
        onConfirm={handleRevokeAccess}
      />
      {shareType === SharedItemShareType.EQUIPMENT &&
        isEditPermissionsDialogOpen && (
          <ChangeUserPermissionsDialog
            equipment={item as IEquipmentEntry}
            userAccess={userAccess}
            onClose={handleEditPermissionsClose}
          />
        )}
      {shareType === SharedItemShareType.FLEET &&
        isEditPermissionsDialogOpen && (
          <ChangeUserFleetPermissionsDialog
            fleet={item as SharedItem}
            userAccess={userAccess}
            onClose={handleEditPermissionsClose}
          />
        )}

      <EllipsisMenu actions={actions} />
    </>
  );
};

export default OperatorDetailsActions;
